import React, { useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { AuthContext } from 'app/shared/context/Auth';
import { Responsive } from 'app/shared/components/atoms';
import { Box } from 'app/shared/components/atoms';
import { ModalButtonsSection } from 'app/shared/components/molecules';
import { useTrackProductAdded } from 'app/fan/utils';
import { trackSuccessfulOrder } from 'app/fan/utils/checkout';
import { CREATE_ORDER } from 'app/fan/graphql/orders/mutations';
import { useEventPageContext } from 'app/fan/context/EventPage';
import { Drawer } from 'app/fan/components/molecules';
import { EventPageTemplate } from 'app/fan/components/templates';
import {
  ApplyFlowCheckoutWidget,
  AttendeeDetailsForm,
  ConfirmCantGoModal,
  GuestShareLinkModal,
} from 'app/fan/components/pages/Event/components';
import { useApplyFlowPage } from 'app/fan/components/pages/Event/eventUtils';

export const CompetitionWinnerEventPage = () => {
  const { formatMessage } = useIntl();
  const [searchParams] = useSearchParams();
  const pageAction = searchParams.get('action');
  const { user } = useContext(AuthContext);
  const { event, eventAttendee, refetchEventAttendee } = useEventPageContext();
  const {
    isCheckoutDrawerOpen,
    setIsCheckoutDrawerOpen,
    openCheckoutDrawer,
    onDrawerClose,
    numberOfTickets,
    setNumberOfTickets,
    onError,
  } = useApplyFlowPage({
    checkoutDrawerOpenByDefault: pageAction === 'confirm',
  });

  const attendeeState = eventAttendee?.currentState;

  const [isConfirmCantGoModalOpen, setIsConfirmCantGoModalOpen] = useState(
    pageAction === 'cant_go'
  );
  const [isClaimTicketsLoading, setIsClaimTicketsLoading] = useState(false);

  const [createOrder] = useMutation(CREATE_ORDER);
  const { trackProductAdded } = useTrackProductAdded(event, true);

  const canClaimTickets = attendeeState === 'guestlisted';
  const hasConfirmedTickets =
    attendeeState === 'confirmed' || attendeeState === 'reserved';
  const canDeclineTickets =
    attendeeState === 'guestlisted' || hasConfirmedTickets;

  const [showShareLinkModal, setShowShareLinkModal] = useState(false);
  useEffect(() => {
    if (hasConfirmedTickets) {
      setTimeout(() => {
        setShowShareLinkModal(true);
      }, 3000);
    }
  }, [attendeeState]);

  // We will never run into this scenario, but include this code so that
  // Typescript knows to expect this to be present
  if (!event || !user || !eventAttendee) {
    return null;
  }

  async function onClickClaimTickets() {
    setIsClaimTicketsLoading(true);
    try {
      trackProductAdded && trackProductAdded();
      const { data } = await createOrder({
        variables: {
          eventId: Number(event?.id),
          numTickets: numberOfTickets,
          stripePaymentId: '', // BE expects a string, but it can be empty (for unpaid events)
          billingRegion: '', // BE expects a string, but it can be empty (for unpaid events)
          isVisaFreeTicketRedeemed: false,
          checkoutVersion: 2,
          isExpressCheckout: true,
        },
      });

      const orderId = data?.createOrder?.id;
      if (event && orderId) {
        trackSuccessfulOrder({
          orderId,
          event,
          numberOfTickets,
          finalPrice: 0,
          fee: 0,
        });
        refetchEventAttendee && (await refetchEventAttendee());
      } else {
        onError();
      }
    } catch (e) {
      console.log(e);
      onError();
    }
    setIsCheckoutDrawerOpen(false);
    setIsClaimTicketsLoading(false);
  }

  const modalButtonsSectionProps = {
    primaryCtaText: formatMessage({ id: 'event.applyFlow.claimTickets' }),
    secondaryCtaText: formatMessage({ id: 'event.applyFlow.cantGo' }),
    onClickSecondaryCta: () => setIsConfirmCantGoModalOpen(true),
    isPrimaryCtaFullWidth: true,
    isPrimaryCtaDisabled: !canClaimTickets,
    isSecondaryCtaDisabled: !canDeclineTickets,
    isPrimaryCtaLoading: isClaimTicketsLoading,
    allowPromoCode: false,
  };

  const checkoutWidgetProps = {
    ...modalButtonsSectionProps,
    event,
    numberOfTickets,
    setNumberOfTickets,
    onClickPrimaryCta: onClickClaimTickets,
    primaryCtaDataQaid:
      'event-page-competition-winner-checkout-widget-claim-tickets',
    secondaryCtaDataQaid:
      'event-page-competition-winner-checkout-widget-cant-go',
  };

  return (
    <EventPageTemplate
      data-qaid="event-page-competition-winner"
      event={event}
      attendeeState={eventAttendee.currentState}
      numTickets={numberOfTickets}
      secondColumn={<ApplyFlowCheckoutWidget {...checkoutWidgetProps} />}
    >
      <Box data-qaid="event-page-first-column-children">
        {hasConfirmedTickets && (
          <AttendeeDetailsForm
            ticketPurchaserName={user.firstName}
            eventAttendee={eventAttendee}
          />
        )}
        <Responsive hideUp="smallDesktop">
          <>
            <Box py={4} px={6}>
              <ModalButtonsSection
                {...modalButtonsSectionProps}
                onClickPrimaryCta={openCheckoutDrawer}
                primaryCtaDataQaid="event-page-competition-winner-checkout-widget-trigger"
                secondaryCtaDataQaid="event-page-competition-winner-cant-go-modal-trigger"
              />
            </Box>
            <Drawer
              isOpen={isCheckoutDrawerOpen}
              setIsOpen={setIsCheckoutDrawerOpen}
              onClose={onDrawerClose}
            >
              <ApplyFlowCheckoutWidget {...checkoutWidgetProps} />
            </Drawer>
          </>
        </Responsive>
      </Box>
      <ConfirmCantGoModal
        event={event}
        isOpen={isConfirmCantGoModalOpen}
        setIsOpen={setIsConfirmCantGoModalOpen}
        onError={onError}
      />
      <GuestShareLinkModal
        isOpen={showShareLinkModal}
        setIsOpen={setShowShareLinkModal}
        event={event}
        ticketPurchaserName={user.firstName}
        ticketPurchaserEventAttendeeId={eventAttendee.id}
      />
    </EventPageTemplate>
  );
};
